import { pdkAxios } from '@/lib/pdk-axios';
import {
  CoRegistrant,
  CustomerAddress,
  PrimaryCustomer,
} from '@/models/registry';
import type {
  Address as CustomerAddressType,
  Customer as CustomerType,
} from '@/models/types';
import { makeAutoObservable } from 'mobx';
import { db } from '../db';
import type {
  DeltaPacketRecord,
  FullHydrateResponse,
  SyncResponse,
} from '../hydrate';
import { hydrateFromWorker } from '../hydrate-worker-utils';
import type { BaseStore, StoreHydrationDelegator } from './base';

class PrimaryCustomerStore implements BaseStore<PrimaryCustomer, CustomerType> {
  store: Map<string, PrimaryCustomer> = new Map();

  storeIndexedByForeignKey: Map<string, PrimaryCustomer> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  private setCustomerField = <K extends keyof CustomerType>(
    customer: PrimaryCustomer,
    key: K,
    value: CustomerType[K],
  ) => {
    (customer as Pick<PrimaryCustomer, K>)[key] = value as PrimaryCustomer[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<CustomerType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.deal_customers.delete(delta.record_id);
      }
      this.remove({ id: delta.record_id, dealId: delta.data.deal_id });
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.deal_customers.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.deal_customers.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.deal_customers.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<CustomerType>;
  }) {
    const existingCustomer = this.getById(delta.record_id);
    if (!existingCustomer) return;

    const entries = Object.entries(delta.data) as [
      keyof CustomerType,
      CustomerType[keyof CustomerType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingCustomer[key] === newValue) {
        continue;
      }

      this.setCustomerField(existingCustomer, key, newValue);
    }
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<CustomerType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<CustomerType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.deal_customers.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.store.delete(delta.record_id);
      return;
    }

    if (delta.action === 'archive') {
      this.remove({ id: delta.record_id, dealId: delta.data.deal_id });
      return;
    }

    this.upsert(delta.data);
  }

  getById(id: string): PrimaryCustomer | undefined {
    return this.store.get(id);
  }

  add(customer: CustomerType) {
    const primaryCustomer = new PrimaryCustomer(customer);
    // todo: this is a hack to prevent co-registrants from being set as primary customers
    if (primaryCustomer.customer_type !== 'primary') {
      return;
    }

    this.store.set(primaryCustomer.id, primaryCustomer);
    // Index by foreign key for O(1) lookups
    this.storeIndexedByForeignKey.set(primaryCustomer.deal_id, primaryCustomer);
  }

  save(customer: PrimaryCustomer) {
    this.store.set(customer.id, customer);
    // Index by foreign key for O(1) lookups
    this.storeIndexedByForeignKey.set(customer.deal_id, customer);
  }

  upsert(customer: CustomerType) {
    const existingCustomer = this.getById(customer.id);
    if (existingCustomer) {
      this.replace({ id: customer.id, customer });
    } else {
      this.add(customer);
    }
  }

  replace({ id, customer }: { id: string; customer: CustomerType }) {
    const existingCustomer = this.getById(id);
    if (!existingCustomer) return;
    existingCustomer.full_name = customer.full_name;
    existingCustomer.email = customer.email;
    existingCustomer.mobile_number = customer.mobile_number;
    this.save(existingCustomer);
  }

  remove({ id, dealId }: { id: string; dealId: string }) {
    this.store.delete(id);
    this.storeIndexedByForeignKey.delete(dealId);

    // remove from dexie
    db.deal_customers.delete(id);
  }

  // actions
  updateFullName({
    id,
    fullName,
  }: {
    id: CustomerType['id'];
    fullName: CustomerType['full_name'];
  }) {
    const customer = this.getById(id);
    if (!customer) return;
    customer.full_name = fullName;
    this.save(customer);
  }

  updateEmail({
    id,
    email,
  }: {
    id: CustomerType['id'];
    email: CustomerType['email'];
  }) {
    const customer = this.getById(id);
    if (!customer) return;
    customer.email = email;
    this.save(customer);
  }

  updateMobileNumber({
    id,
    mobileNumber,
  }: {
    id: CustomerType['id'];
    mobileNumber: CustomerType['mobile_number'];
  }) {
    const customer = this.getById(id);
    if (!customer) return;
    customer.mobile_number = mobileNumber;
    this.save(customer);
  }

  async hydrateLocal(): Promise<void> {
    await hydrateFromWorker({
      tableName: 'deal_customers',
      entity: 'deal_customers',
      modelStore: primaryCustomerStore,
    });
  }

  async hydrateDeal(dealId: CustomerType['deal_id']): Promise<void> {
    const customers = await db.deal_customers.toArray();
    customers.forEach((customer) => {
      if (customer.deal_id === dealId) this.upsert(customer);
    });
  }
}

export const primaryCustomerStore = new PrimaryCustomerStore();

class CoRegistrantStore implements BaseStore<CoRegistrant, CustomerType> {
  store: Map<string, CoRegistrant> = new Map();

  storeIndexedByForeignKey: Map<string, CoRegistrant> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  private setCustomerField = <K extends keyof CustomerType>(
    customer: CoRegistrant,
    key: K,
    value: CustomerType[K],
  ) => {
    (customer as Pick<CoRegistrant, K>)[key] = value as CoRegistrant[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<CustomerType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.deal_customers.delete(delta.record_id);
      }
      this.remove({ id: delta.record_id, dealId: delta.data.deal_id });
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.deal_customers.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.deal_customers.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.deal_customers.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<CustomerType>;
  }) {
    const existingCustomer = this.getById(delta.record_id);
    if (!existingCustomer) return;

    const entries = Object.entries(delta.data) as [
      keyof CustomerType,
      CustomerType[keyof CustomerType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingCustomer[key] === newValue) {
        continue;
      }

      this.setCustomerField(existingCustomer, key, newValue);
    }
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<CustomerType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<CustomerType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.deal_customers.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.remove({ id: delta.record_id, dealId: delta.data.deal_id });
      return;
    }

    if (delta.action === 'archive') {
      this.remove({ id: delta.record_id, dealId: delta.data.deal_id });
      return;
    }

    this.upsert(delta.data);
  }

  getById(id: string): CoRegistrant | undefined {
    return this.store.get(id);
  }

  add(customer: CustomerType) {
    const coRegistrant = new CoRegistrant(customer);
    if (coRegistrant.customer_type !== 'co_registrant') {
      return;
    }

    this.store.set(coRegistrant.id, coRegistrant);
    // Index by foreign key for O(1) lookups
    this.storeIndexedByForeignKey.set(coRegistrant.deal_id, coRegistrant);
  }

  save(customer: CoRegistrant) {
    this.store.set(customer.id, customer);
    // Index by foreign key for O(1) lookups
    this.storeIndexedByForeignKey.set(customer.deal_id, customer);
  }

  upsert(customer: CustomerType) {
    const existingCustomer = this.getById(customer.id);
    if (existingCustomer) {
      this.replace({ id: customer.id, customer });
    } else {
      this.add(customer);
    }
  }

  replace({ id, customer }: { id: string; customer: CustomerType }) {
    const existingCustomer = this.getById(id);
    if (!existingCustomer) return;
    existingCustomer.full_name = customer.full_name;
    existingCustomer.email = customer.email;
    existingCustomer.mobile_number = customer.mobile_number;
    this.save(existingCustomer);
  }

  remove({ id, dealId }: { id: string; dealId: string }) {
    this.store.delete(id);
    this.storeIndexedByForeignKey.delete(dealId);

    // remove from dexie
    db.deal_customers.delete(id);
  }

  updateFullName({
    id,
    fullName,
  }: {
    id: CustomerType['id'];
    fullName: CustomerType['full_name'];
  }) {
    const customer = this.getById(id);
    if (!customer) return;
    customer.full_name = fullName;
    this.save(customer);
  }

  updateEmail({
    id,
    email,
  }: {
    id: CustomerType['id'];
    email: CustomerType['email'];
  }) {
    const customer = this.getById(id);
    if (!customer) return;
    customer.email = email;
    this.save(customer);
  }

  updateMobileNumber({
    id,
    mobileNumber,
  }: {
    id: CustomerType['id'];
    mobileNumber: CustomerType['mobile_number'];
  }) {
    const customer = this.getById(id);
    if (!customer) return;
    customer.mobile_number = mobileNumber;
    this.save(customer);
  }

  hydrateLocal(): Promise<void> {
    return hydrateFromWorker({
      tableName: 'deal_customers',
      entity: 'deal_customers',
      modelStore: coRegistrantStore,
    });
  }

  async hydrateDeal(dealId: CustomerType['deal_id']): Promise<void> {
    const customers = await db.deal_customers.toArray();
    customers.forEach((customer) => {
      if (customer.deal_id === dealId) this.upsert(customer);
    });
  }
}

export const coRegistrantStore = new CoRegistrantStore();

class CustomerAddressStore
  implements BaseStore<CustomerAddress, CustomerAddressType>
{
  store: Map<string, CustomerAddress> = new Map();

  // indexed by customer_id
  storeIndexedByForeignKey: Map<string, CustomerAddress[]> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  private setCustomerAddressField = <K extends keyof CustomerAddressType>(
    customerAddress: CustomerAddress,
    key: K,
    value: CustomerAddressType[K],
  ) => {
    (customerAddress as Pick<CustomerAddress, K>)[key] =
      value as CustomerAddress[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<CustomerAddressType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.deal_customer_addresses.delete(delta.record_id);
      }
      this.remove({ id: delta.record_id, customerId: delta.data.customer_id });
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.deal_customer_addresses.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.deal_customer_addresses.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.deal_customer_addresses.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<CustomerAddressType>;
  }) {
    const existingAddress = this.getById(delta.record_id);
    if (!existingAddress) return;

    const entries = Object.entries(delta.data) as [
      keyof CustomerAddressType,
      CustomerAddressType[keyof CustomerAddressType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingAddress[key] === newValue) {
        continue;
      }

      this.setCustomerAddressField(existingAddress, key, newValue);
    }

    this.save(existingAddress);
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<CustomerAddressType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<CustomerAddressType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.deal_customer_addresses.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.remove({ id: delta.record_id, customerId: delta.data.customer_id });
      return;
    }

    if (delta.action === 'archive') {
      this.remove({ id: delta.record_id, customerId: delta.data.customer_id });
      return;
    }

    this.upsert(delta.data);
  }

  get all() {
    return Array.from(this.store.values());
  }

  getById(id: string): CustomerAddress | undefined {
    return this.store.get(id);
  }

  add(address: CustomerAddressType) {
    const customerAddress = new CustomerAddress(address);
    this.store.set(customerAddress.id, customerAddress);
    // Index by foreign key for O(1) lookups
    const arr =
      this.storeIndexedByForeignKey.get(customerAddress.customer_id) || [];
    arr.push(customerAddress);
    this.storeIndexedByForeignKey.set(customerAddress.customer_id, arr);
  }

  save(customerAddress: CustomerAddress) {
    this.store.set(customerAddress.id, customerAddress);
    // Index by foreign key for O(1) lookups
    const arr =
      this.storeIndexedByForeignKey.get(customerAddress.customer_id) || [];
    this.storeIndexedByForeignKey.set(
      customerAddress.customer_id,
      arr.map((a) => (a.id === customerAddress.id ? customerAddress : a)),
    );
  }

  upsert(customerAddress: CustomerAddressType) {
    const existingCustomerAddress = this.getById(customerAddress.id);
    if (existingCustomerAddress) {
      this.replace({ id: customerAddress.id, customerAddress });
    } else {
      this.add(customerAddress);
    }
  }

  replace({
    id,
    customerAddress,
  }: {
    id: string;
    customerAddress: CustomerAddressType;
  }) {
    const existingCustomerAddress = this.getById(id);
    if (!existingCustomerAddress) return;
    existingCustomerAddress.address_line_1 = customerAddress.address_line_1;
    existingCustomerAddress.address_line_2 = customerAddress.address_line_2;
    existingCustomerAddress.city = customerAddress.city || '';
    existingCustomerAddress.state = customerAddress.state || '';
    existingCustomerAddress.postal_code = customerAddress.postal_code || '';
    existingCustomerAddress.address_type = customerAddress.address_type || '';

    this.save(existingCustomerAddress);
  }

  remove({ id, customerId }: { id: string; customerId: string }) {
    this.store.delete(id);
    const arr = this.storeIndexedByForeignKey.get(customerId) || [];
    this.storeIndexedByForeignKey.set(
      customerId,
      arr.filter((a) => a.id !== id),
    );

    // remove from dexie
    db.deal_customer_addresses.delete(id);
  }

  updateByCustomerIdAndAddressType({
    customerId,
    addressType,
    address,
  }: {
    customerId: string;
    addressType: string;
    address: CustomerAddressType;
  }) {
    const customerAddress = this.storeIndexedByForeignKey
      .get(customerId)
      ?.find((a) => a.address_type === addressType);
    if (!customerAddress) return;
    this.replace({ id: customerAddress.id, customerAddress: address });
  }

  hydrateLocal(): Promise<void> {
    return hydrateFromWorker({
      tableName: 'deal_customer_addresses',
      entity: 'deal_customer_addresses',
      modelStore: customerAddressStore,
    });
  }

  async hydrateDeal(
    customerIds: CustomerAddressType['customer_id'][],
  ): Promise<void> {
    const addresses = await db.deal_customer_addresses.toArray();
    addresses.forEach((address) => {
      if (customerIds.includes(address.customer_id)) this.upsert(address);
    });
  }
}

export const customerAddressStore = new CustomerAddressStore();

class CustomerStoreHydrationDelegator
  implements StoreHydrationDelegator<CustomerType>
{
  async hydrate({
    fullHydrateEndpoint,
    deltaSyncEndpoint,
  }: {
    fullHydrateEndpoint: string;
    deltaSyncEndpoint: string;
  }) {
    const checkpoint = await db.syncCheck.get('deal_customers');

    if (!checkpoint) {
      await this.fullHydrateRepresentedStores({
        fullHydrateEndpoint,
        key: 'deal_customers',
        dexieTable: db.deal_customers,
      });
      return;
    }
    const url = `${deltaSyncEndpoint}?sinceSyncId=${checkpoint.lastSyncId}`;

    const { entries, firstSyncId, lastSyncId } = (
      await pdkAxios.get<SyncResponse<CustomerType>>(url)
    ).data;

    // 3) If we're behind, do a full hydrate
    if (checkpoint.lastSyncId && checkpoint.lastSyncId < firstSyncId) {
      await this.fullHydrateRepresentedStores({
        fullHydrateEndpoint,
        key: 'deal_customers',
        dexieTable: db.deal_customers,
      });
      return;
    }

    await db.transaction('rw', db.deal_customers, db.syncCheck, async () => {
      for (const { action, data, record_id } of entries) {
        if (action === 'delete' || action === 'archive') {
          await db.deal_customers.delete(record_id);
        } else if (action === 'update') {
          await db.deal_customers.update(record_id, data);
        } else {
          await db.deal_customers.put(data, record_id);
        }
      }
      if (lastSyncId > (checkpoint.lastSyncId || 0)) {
        await db.syncCheck.put({
          collection: 'deal_customers',
          firstSyncId,
          lastSyncId,
        });
      }
    });

    const all = await db.deal_customers.toArray();
    all.forEach((customer) => {
      if (customer.customer_type === 'primary') {
        primaryCustomerStore.upsert(customer);
      } else {
        coRegistrantStore.upsert(customer);
      }
    });
  }

  async fullHydrateRepresentedStores({
    fullHydrateEndpoint,
    key,
    dexieTable,
  }: {
    fullHydrateEndpoint: string;
    key: string;
    dexieTable: any;
  }) {
    const resp =
      await pdkAxios.get<FullHydrateResponse<CustomerType>>(
        fullHydrateEndpoint,
      );

    const { data, firstSyncId, lastSyncId } = resp.data;

    await db.transaction('rw', dexieTable, db.syncCheck, async () => {
      await dexieTable.clear();
      await dexieTable.bulkPut(data);
      await db.syncCheck.put({
        collection: key,
        firstSyncId,
        lastSyncId,
      });
      data.forEach((customer) => {
        if (customer.customer_type === 'primary') {
          primaryCustomerStore.upsert(customer);
        } else {
          coRegistrantStore.upsert(customer);
        }
      });
    });
  }
}

export const customerStoreHydrationDelegator =
  new CustomerStoreHydrationDelegator();
